import { zodResolver } from "@hookform/resolvers/zod";
import { Loader, Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCreateCustomVerificationTypeMutation } from "@/data/mutations";
import { CustomVerificationType } from "@/data/types";

const createVerificationTypeFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
});

export const CreateVerificationTypeDialog = () => {
  const [open, setOpen] = useState(false);

  const createVerificationTypeMutation =
    useCreateCustomVerificationTypeMutation();
  const form = useForm({
    resolver: zodResolver(createVerificationTypeFormSchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const onSubmit = async (
    values: z.infer<typeof createVerificationTypeFormSchema>,
  ) => {
    try {
      await createVerificationTypeMutation.mutateAsync({
        name: values.name,
        description: values.description,
      } as CustomVerificationType);
    } catch (error) {
      console.error(error);
    } finally {
      setOpen(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" /> Create Verification Type
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <AlertDialogHeader>
              <AlertDialogTitle>Create Verification Type</AlertDialogTitle>
              <AlertDialogDescription>
                Create a new verification type for your organization.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 my-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Verification Type*</FormLabel>
                    <FormControl>
                      <Input placeholder="Sent to warehouse" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="This verification happens when the item is finished product and sent to the warehouse for storage."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <Button
                type="submit"
                disabled={createVerificationTypeMutation.isPending}
              >
                {createVerificationTypeMutation.isPending ? (
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Create
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
};
